import {ReactNode, useCallback, useEffect, useReducer, useRef, useState} from "react";
import {getDungeonSettings, getEncounterDungeon, saveDungeonSettings} from "@/api/EncounterApi.ts";
import {useEncounter} from "@/encounter/EncounterHooks.ts";
import {DungeonContext} from "@/encounter/dungeon/DungeonHooks.ts";
import {DungeonSettings} from "@/encounter/model/DungeonSettings.ts";
import {Dungeon} from "@/dungeons/model/Dungeon.ts";
import {useDungeonSettingsRealtime} from "@/encounter/dungeon/useDungeonSettingsRealtime.ts";
import {useDebounceCallback} from "usehooks-ts";
import {useBackgroundErrorNotification} from "@/api/useBackgroundErrorNotification.ts";
import {srcUrl} from "@/api/ImageApi.ts";
import {useInitialImages} from "@/images/useInitialImages.ts";

type DungeonState = {
    dungeonSettings?: DungeonSettings;
    dungeon?: Dungeon;
};

type DungeonAction =
    | { type: "settings"; settings: DungeonSettings }
    | { type: "dungeon"; dungeon: Dungeon }
    | { type: "reset" };

const initialState: DungeonState = {};

function reducer(state: DungeonState, action: DungeonAction): DungeonState {
    switch (action.type) {
        case "settings":
            return {
                dungeonSettings: action.settings,
                dungeon: state.dungeon?.id === action.settings.dungeonId
                    ? state.dungeon
                    : undefined,
            };
        case "dungeon":
            return {...state, dungeon: action.dungeon};
        case "reset":
            return initialState;
    }
}

export function DungeonProvider({children}: { children: ReactNode }) {
    const encounter = useEncounter();
    const subscribeToUpdates = useDungeonSettingsRealtime(encounter.id);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [initialLoad, setInitialLoad] = useState<{
        settingsEncounterId?: string;
        dungeonId?: string;
        settingsError: boolean;
        dungeonError: boolean;
    }>({settingsError: false, dungeonError: false});
    const settingsSaveRevision = useRef(0);
    const settingsSaveQueue = useRef<Promise<unknown>>(Promise.resolve());
    const localSettingsPending = useRef(false);
    const {report, recover} = useBackgroundErrorNotification();
    const dungeonId = encounter.dungeonSettings.dungeonId;
    const settingsLoading = initialLoad.settingsEncounterId !== encounter.id;
    const dungeonLoading = Boolean(dungeonId && initialLoad.dungeonId !== dungeonId);
    const initialError = initialLoad.settingsError || initialLoad.dungeonError;
    const initialLoading = !initialError && (settingsLoading || dungeonLoading);

    const activeLevel = state.dungeon?.levels.find(level => level.id === state.dungeonSettings?.levelId);
    const imagesReady = useInitialImages(activeLevel ? [srcUrl(activeLevel.map.image)] : [],
        !initialLoading && !initialError && Boolean(state.dungeon && state.dungeonSettings));

    const persistSettings = useCallback((settings: DungeonSettings): Promise<void> => {
        const revision = ++settingsSaveRevision.current;
        const save = settingsSaveQueue.current.catch(() => undefined)
            .then(() => saveDungeonSettings(encounter, settings));
        settingsSaveQueue.current = save.catch(() => undefined);
        return save.then(saved => {
            if (revision === settingsSaveRevision.current) dispatch({type: "settings", settings: saved});
        }).finally(() => {
            if (revision === settingsSaveRevision.current) localSettingsPending.current = false;
        });
    }, [encounter]);
    const persistSettingsInBackground = useCallback((settings: DungeonSettings) => {
        void persistSettings(settings)
            .then(recover)
            .catch(error => report(error, "Die Dungeon-Einstellungen konnten nicht gespeichert werden."));
    }, [persistSettings, recover, report]);
    const debouncedPersistSettings = useDebounceCallback(persistSettingsInBackground, 200, {maxWait: 500});

    const previewDungeonSettings = useCallback((settings: DungeonSettings) => {
        if (settings.dungeonId !== dungeonId) throw new Error("Encounter dungeon cannot be changed");
        settingsSaveRevision.current++;
        localSettingsPending.current = true;
        dispatch({type: "settings", settings});
        debouncedPersistSettings(settings);
    }, [debouncedPersistSettings, dungeonId]);

    useEffect(() => {
        let active = true;
        settingsSaveRevision.current++;
        localSettingsPending.current = false;
        debouncedPersistSettings.cancel();
        dispatch({type: "reset"});

        let receivedRealtimeUpdate = false;
        const unsubscribe = subscribeToUpdates(settings => {
            if (!active) return;
            if (settings.dungeonId !== dungeonId) {
                setInitialLoad(current => ({...current, settingsError: true}));
                return;
            }
            receivedRealtimeUpdate = true;
            if (!localSettingsPending.current) dispatch({type: "settings", settings});
            setInitialLoad(current => ({
                settingsEncounterId: encounter.id,
                dungeonId: current.dungeonId,
                settingsError: false,
                dungeonError: current.dungeonError,
            }));
        });

        void getDungeonSettings(encounter).then(settings => {
            if (active && !receivedRealtimeUpdate) {
                if (settings.dungeonId !== dungeonId) {
                    setInitialLoad(current => ({...current, settingsError: true}));
                    return;
                }
                dispatch({type: "settings", settings});
                setInitialLoad(current => ({
                    settingsEncounterId: encounter.id,
                    dungeonId: current.dungeonId,
                    settingsError: false,
                    dungeonError: current.dungeonError,
                }));
            }
        }).catch(() => {
            if (active && !receivedRealtimeUpdate) {
                setInitialLoad(current => ({...current, settingsEncounterId: encounter.id, settingsError: true}));
            }
        });

        return () => {
            active = false;
            unsubscribe();
        };
    }, [debouncedPersistSettings, dungeonId, encounter, subscribeToUpdates]);

    useEffect(() => {
        let active = true;
        void getEncounterDungeon(encounter).then(dungeon => {
            if (active) {
                if (dungeon.id !== dungeonId) {
                    setInitialLoad(current => ({...current, dungeonError: true}));
                    return;
                }
                dispatch({type: "dungeon", dungeon});
                setInitialLoad(current => ({...current, dungeonId, dungeonError: false}));
            }
        }).catch(() => {
            if (active) {
                setInitialLoad(current => ({...current, dungeonId, dungeonError: true}));
            }
        });
        return () => {
            active = false;
        };
    }, [dungeonId, encounter]);

    const setDungeonSettings = useCallback(async (settings: DungeonSettings) => {
        if (settings.dungeonId !== dungeonId) throw new Error("Encounter dungeon cannot be changed");
        debouncedPersistSettings.cancel();
        localSettingsPending.current = true;
        dispatch({type: "settings", settings});
        await persistSettings(settings);
    }, [debouncedPersistSettings, dungeonId, persistSettings]);

    if (initialError) throw new Error("Dungeon konnte nicht geladen werden.");
    if (initialLoading || !state.dungeon || !state.dungeonSettings ||
        state.dungeon.id !== state.dungeonSettings.dungeonId || !imagesReady) return null;

    return <DungeonContext.Provider value={{
        dungeon: state.dungeon,
        dungeonSettings: state.dungeonSettings,
        previewDungeonSettings,
        setDungeonSettings,
    }}>
        {children}
    </DungeonContext.Provider>;
}
